import { h, Fragment } from 'preact';
import { useRef, useCallback, useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';

import style from './style.css';

import useViewportRelation from '../../hooks/useViewportRelation';
import globalErrorHandler from '../../utils/errorHandler.js';

export function makePersistantAutoloadingList(
  dataSource,
  limit,
  LazyView,
  View,
  viewProps
) {
  return makeLazyList(dataSource, limit, LazyView, View, viewProps, true, true);
}

export function makeAutoloadingList(
  dataSource,
  limit,
  LazyView,
  View,
  viewProps
) {
  return makeLazyList(
    dataSource,
    limit,
    LazyView,
    View,
    viewProps,
    false,
    true
  );
}

export function makePersistantLazyList(
  dataSource,
  limit,
  LazyView,
  View,
  viewProps
) {
  return makeLazyList(
    dataSource,
    limit,
    LazyView,
    View,
    viewProps,
    true,
    false
  );
}

export function makeSimpleLazyList(
  dataSource,
  limit,
  LazyView,
  View,
  viewProps
) {
  return makeLazyList(dataSource, limit, LazyView, View, viewProps);
}

function getInitialState() {
  return {
    loading: false,
    hasMore: true,
    items: [],
  };
}

export function makeLazyList(
  dataSource,
  limit,
  LazyView,
  View,
  viewProps,
  preserve,
  autoload
) {
  const state = signal(getInitialState());

  function updateState(override) {
    state.value = { ...state.value, ...override };
  }

  function renderLazyItem(_value, index) {
    return <LazyView key={index} />;
  }

  function renderItem(rawItem, index) {
    return h(View, { key: rawItem._id, ...viewProps, index, model: rawItem });
  }

  const lazyList = Array(limit).fill(null).map(renderLazyItem);

  function onError(error) {
    updateState({
      loading: false,
      hasMore: false,
    });
    globalErrorHandler(error);
  }

  function onItems(newItems) {
    const oldState = state.value;
    updateState({
      loading: false,
      items: [...oldState.items, ...newItems],
      hasMore: newItems.length === limit,
    });
  }

  function loadItems() {
    const { loading, items } = state.value;
    if (loading) {
      return;
    }

    updateState({ loading: true });
    dataSource(items.length, limit).then(onItems).catch(onError);
  }

  if (autoload) {
    loadItems();
  }

  function reset() {
    if (preserve) {
      return;
    }

    state.value = getInitialState();
  }

  function restart() {
    updateState({ loading: false, hasMore: true, items: [] });
  }

  function LazyList(props) {
    const { restartCount } = props;
    const signalElement = useRef(null);

    const { items, loading, hasMore } = state.value;

    const loadMore = useCallback(function loadMore() {
      loadItems();
    }, []);

    useEffect(
      function onRestartChange() {
        restart();
      },
      [restartCount]
    );

    useViewportRelation(loadMore, signalElement, !loading && hasMore);

    useEffect(function onUnmount() {
      return reset;
    }, []);

    return (
      <Fragment>
        {items.map(renderItem)}
        {loading && lazyList}
        <div ref={signalElement} class={style.lazyLoadSignal}></div>
      </Fragment>
    );
  }

  return LazyList;
}
